// No-code embed entry (built to dist/acountpay.js, exposed as window.AcountPay).
//
// Usage on a merchant site:
//   <script src="https://cdn.acountpay.com/sdk/acountpay.js"
//           data-button-id="btn_..."></script>
//
// The script auto-renders the configured button immediately after itself
// (Razorpay-style) and opens the popup checkout on click.

import {
  PaymentButtonConfig,
  CheckoutResult,
  fetchButtonConfig,
  openPopupShell,
  startCheckout,
} from './checkout';

/**
 * True when running inside the Squarespace (or similar) site editor preview
 * iframe. Buttons render but don't start real checkouts there, so merchants
 * can't accidentally trigger payments while editing their site.
 */
function isEditorPreview(): boolean {
  try {
    if (window.self === window.top) return false;
    const body = document.body;
    if (
      body &&
      ['sqs-edit-mode', 'sqs-edit-mode-active', 'sqs-is-page-editing'].some(
        (cls) => body.classList.contains(cls),
      )
    ) {
      return true;
    }
    // Squarespace preview iframe: site has the SQUARESPACE_CONTEXT global and
    // is framed by a squarespace.com config page.
    if (
      (window as any).Static?.SQUARESPACE_CONTEXT &&
      /squarespace\.com/i.test(document.referrer)
    ) {
      return true;
    }
  } catch {
    /* cross-origin access errors mean we can't tell; assume live site */
  }
  return false;
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

function themeStyles(config: PaymentButtonConfig): string {
  const base =
    'appearance:none;border:0;border-radius:10px;padding:12px 22px;font-size:15px;font-weight:600;cursor:pointer;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;line-height:1;transition:opacity .15s ease;display:inline-flex;align-items:center;gap:8px';
  if (config.theme === 'dark') {
    return `${base};background:#0F172A;color:#fff`;
  }
  if (config.theme === 'brand') {
    return `${base};background:${config.primaryColor};color:#fff`;
  }
  return `${base};background:#fff;color:#0F172A;border:1px solid #E2E8F0`;
}

function buttonText(config: PaymentButtonConfig): string {
  return config.label || 'Betal med bank';
}

function runInlineHandler(el: HTMLElement, attr: string, detail: CheckoutResult) {
  const code = el.getAttribute(attr);
  if (!code) return;
  try {
    // eslint-disable-next-line no-new-func
    new Function('detail', code).call(el, detail);
  } catch (err) {
    console.error(`[AcountPay] error in ${attr} handler:`, err);
  }
}

function dispatch(el: HTMLElement, type: string, detail: CheckoutResult) {
  el.dispatchEvent(new CustomEvent(type, { detail, bubbles: true }));
}

async function handleClick(
  trigger: HTMLElement,
  config: PaymentButtonConfig,
  getAmount: () => number | undefined,
) {
  // Validate synchronously, before opening the popup shell.
  let amount: number | undefined;
  if (config.amountType === 'customer') {
    amount = getAmount();
    if (!amount || amount <= 0) {
      dispatch(trigger, 'acountpay:error', { status: 'failed' });
      runInlineHandler(trigger, 'data-on-error', { status: 'failed' });
      console.error('[AcountPay] checkout error: please enter a valid amount');
      return;
    }
  }

  // Open the popup synchronously within the click gesture so blockers allow
  // it (returns null on mobile, where checkout redirects instead).
  const popup = openPopupShell();

  const original = trigger.textContent;
  trigger.setAttribute('disabled', 'true');
  (trigger as HTMLButtonElement).style.opacity = '0.6';
  trigger.textContent = 'Loading...';

  try {
    const result = await startCheckout(config.buttonId, { amount, popup });

    if (result.status === 'success') {
      dispatch(trigger, 'acountpay:success', result);
      runInlineHandler(trigger, 'data-on-success', result);
      const successUrl = trigger.getAttribute('data-success-url');
      if (successUrl) window.location.assign(successUrl);
    } else {
      dispatch(trigger, `acountpay:${result.status}`, result);
      if (result.status === 'failed') {
        runInlineHandler(trigger, 'data-on-error', result);
      }
    }
  } catch (err) {
    console.error('[AcountPay] checkout error:', err);
    dispatch(trigger, 'acountpay:error', { status: 'failed' });
    runInlineHandler(trigger, 'data-on-error', { status: 'failed' });
  } finally {
    trigger.removeAttribute('disabled');
    (trigger as HTMLButtonElement).style.opacity = '1';
    trigger.textContent = original;
  }
}

function buildButton(
  config: PaymentButtonConfig,
): { container: HTMLElement; trigger: HTMLElement; getAmount: () => number | undefined } {
  const container = document.createElement('div');
  container.setAttribute('data-acountpay-container', config.buttonId);
  container.style.cssText = 'display:inline-flex;flex-direction:column;gap:8px';

  let amountInput: HTMLInputElement | null = null;
  if (config.amountType === 'customer') {
    amountInput = document.createElement('input');
    amountInput.type = 'number';
    amountInput.min = '0';
    amountInput.step = '0.01';
    amountInput.placeholder = `Amount (${config.currency})`;
    amountInput.style.cssText =
      'padding:10px 12px;border:1px solid #E2E8F0;border-radius:10px;font-size:14px;font-family:inherit';
    container.appendChild(amountInput);
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.style.cssText = themeStyles(config);

  const amountSuffix =
    config.amountType === 'fixed' && config.amount != null
      ? ` ${formatAmount(config.amount, config.currency)}`
      : '';
  button.textContent = `${buttonText(config)}${amountSuffix}`;

  container.appendChild(button);

  return {
    container,
    trigger: button,
    getAmount: () =>
      amountInput && amountInput.value
        ? parseFloat(amountInput.value)
        : undefined,
  };
}

async function mountFromElement(host: HTMLElement, buttonId: string) {
  if (host.getAttribute('data-acountpay-mounted') === 'true') return;
  host.setAttribute('data-acountpay-mounted', 'true');
  try {
    const config = await fetchButtonConfig(buttonId);
    const { container, trigger, getAmount } = buildButton(config);
    // Carry inline handlers from the host element onto the trigger.
    ['data-on-success', 'data-on-error', 'data-success-url'].forEach((attr) => {
      const v = host.getAttribute(attr);
      if (v) trigger.setAttribute(attr, v);
    });
    if (isEditorPreview()) {
      trigger.setAttribute('disabled', 'true');
      (trigger as HTMLButtonElement).style.opacity = '0.7';
      trigger.setAttribute(
        'title',
        'Preview - the payment button is active on your published site',
      );
    } else {
      trigger.addEventListener('click', () => handleClick(trigger, config, getAmount));
    }

    if (host.tagName === 'SCRIPT') {
      host.parentNode?.insertBefore(container, host.nextSibling);
    } else {
      host.innerHTML = '';
      host.appendChild(container);
    }
  } catch (err) {
    console.error('[AcountPay] failed to render payment button:', err);
  }
}

function mountAll() {
  // 1. <script data-button-id> tags (Razorpay-style auto-render).
  document
    .querySelectorAll<HTMLScriptElement>('script[data-button-id]')
    .forEach((script) => {
      const buttonId = script.getAttribute('data-button-id');
      if (buttonId) void mountFromElement(script, buttonId);
    });

  // 2. Explicit placeholder elements, e.g. <div data-acountpay-button="btn_...">.
  document
    .querySelectorAll<HTMLElement>('[data-acountpay-button]')
    .forEach((el) => {
      const buttonId = el.getAttribute('data-acountpay-button');
      if (buttonId) void mountFromElement(el, buttonId);
    });
}

const EMBED_SELECTOR = 'script[data-button-id],[data-acountpay-button]';

/**
 * Re-mount when embed hosts are added after initial load. Site builders like
 * Squarespace load pages via AJAX and inject code blocks dynamically, so the
 * script's initial mountAll() can run before (or without) the host elements
 * being in the DOM. mountFromElement is idempotent via data-acountpay-mounted.
 */
function observeDynamicMounts() {
  if (typeof MutationObserver === 'undefined') return;
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      mountAll();
    }, 50);
  };
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (let i = 0; i < mutation.addedNodes.length; i++) {
        const node = mutation.addedNodes[i];
        if (node.nodeType !== 1) continue;
        const el = node as Element;
        if (el.matches?.(EMBED_SELECTOR) || el.querySelector?.(EMBED_SELECTOR)) {
          schedule();
          return;
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

const AcountPay = {
  mount: mountAll,
  mountButton: mountFromElement,
  checkout: startCheckout,
  getButtonConfig: fetchButtonConfig,
};

if (typeof window !== 'undefined') {
  (window as any).AcountPay = AcountPay;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      mountAll();
      observeDynamicMounts();
    });
  } else {
    mountAll();
    observeDynamicMounts();
  }
}

export default AcountPay;
